/**
 * KTS v1 REST client — typed wrapper around the phased session API.
 *
 * See docs/KTS_PHASED_SESSION_API.md for the contract.
 * Backend: services/kts/src/routes/v1-sessions.ts.
 *
 * Replaces the legacy single-call flow (POST /api/sessions/open + WebSocket
 * + POST /seal) with the phased lifecycle (Run → Prework → Action → Stop →
 * wind-down). Coexists with the legacy client; migrate tool-by-tool.
 */

const KTS_BASE_URL =
  (import.meta.env.VITE_KTS_URL as string | undefined)?.replace(/\/$/, '') ??
  'https://kts.projecttower.org';

// ───────────────────────────────────────────────────────────────────────────
// Domain types — mirror the API spec exactly
// ───────────────────────────────────────────────────────────────────────────

export type SessionStatus =
  | 'tollbooth_pending'
  | 'prework'
  | 'ready'
  | 'action_active'
  | 'action_closing'
  | 'wind_down'
  | 'complete'
  | 'failed'
  | 'abandoned';

export type SessionMode = 'solo' | 'tripod' | 'coop';
export type PartnerRole = 'spotter' | 'timer' | 'filmer' | 'observer';

export interface PhaseReceipt {
  phase: string;
  slot_id: string | null;
  snap_hash: string | null;
  receipt_id: string | null;
  receipt_hash: string | null;
  attested: boolean;
  extra?: Record<string, unknown>;
}

export interface KineticSession {
  session_id: string;
  user_id: string;
  tool_id: string;
  status: SessionStatus | null;
  mode: SessionMode | null;
  partner_id: string | null;
  partner_role: string | null;
  partner_joined_at: string | null;
  phase_receipts: PhaseReceipt[];
  merkle_root: string | null;
  session_summary_hash: string | null;
  primary_yield: number | null;
  duration_s: number | null;
  gec: { y: number | null; x: number | null; gec0: number | null } | null;
  integrity_score: number | null;
  pipeline_class: string | null;
  verdict_summary: Record<string, unknown> | null;
  failure_reason: string | null;
  started_at: string | null;
  action_started_at: string | null;
  action_stopped_at: string | null;
  completed_at: string | null;
}

export interface CanonicalActionEvent {
  action: string;
  timestamp_ms: number;
  score: number;
  reinforced?: boolean;
  block_type?: 'Action' | 'Interaction';
  joint_angles?: Record<string, number> | null;
  device_telemetry?: Record<string, unknown> | null;
  weight?: number;
}

interface OkEnvelope<T = Record<string, unknown>> {
  ok: true;
  session: KineticSession;
  phase_result?: T;
}

interface ErrEnvelope {
  ok: false;
  error: { code: string; message: string; details?: Record<string, unknown> };
  session?: KineticSession | null;
}

export class KtsV1Error extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;
  session?: KineticSession | null;
  constructor(args: {
    code: string;
    message: string;
    status: number;
    details?: Record<string, unknown>;
    session?: KineticSession | null;
  }) {
    super(args.message);
    this.code = args.code;
    this.status = args.status;
    this.details = args.details;
    this.session = args.session;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// HTTP helper
// ───────────────────────────────────────────────────────────────────────────

async function request<TPhaseResult = Record<string, unknown>>(
  path: string,
  init: RequestInit & { authToken: string },
): Promise<OkEnvelope<TPhaseResult>> {
  const { authToken, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set('content-type', 'application/json');
  headers.set('authorization', `Bearer ${authToken}`);

  const res = await fetch(`${KTS_BASE_URL}${path}`, {
    ...rest,
    headers,
  });

  const text = await res.text();
  let parsed: OkEnvelope<TPhaseResult> | ErrEnvelope;
  try {
    parsed = text ? JSON.parse(text) : ({ ok: false, error: { code: 'empty', message: 'empty response' } } as ErrEnvelope);
  } catch {
    throw new KtsV1Error({
      code: 'parse_error',
      message: `KTS v1 ${path} returned non-JSON (${res.status})`,
      status: res.status,
    });
  }

  if (!parsed.ok) {
    throw new KtsV1Error({
      code: parsed.error.code,
      message: parsed.error.message,
      status: res.status,
      details: parsed.error.details,
      session: parsed.session ?? null,
    });
  }
  return parsed;
}

// ───────────────────────────────────────────────────────────────────────────
// Phase calls — one method per spec endpoint
// ───────────────────────────────────────────────────────────────────────────

export interface CreateSessionInput {
  tool_id: string;
  mode?: SessionMode;
  partner_id?: string;
  partner_role?: PartnerRole;
  client_metadata?: Record<string, unknown>;
}

export async function createKineticSession(
  authToken: string,
  input: CreateSessionInput,
): Promise<OkEnvelope<{ tc_deducted: number | null; tollbooth_attested: boolean }>> {
  return request<{ tc_deducted: number | null; tollbooth_attested: boolean }>(
    '/api/v1/sessions',
    {
      method: 'POST',
      body: JSON.stringify(input),
      authToken,
    },
  );
}

export interface PreworkInput {
  setup_video_url?: string | null;
  calibration?: {
    locked: boolean;
    confidence: number;
    baseline?: Record<string, unknown>;
  };
}

export async function runPrework(
  authToken: string,
  sessionId: string,
  input: PreworkInput,
): Promise<OkEnvelope> {
  return request(`/api/v1/sessions/${encodeURIComponent(sessionId)}/prework`, {
    method: 'POST',
    body: JSON.stringify(input),
    authToken,
  });
}

export async function joinSession(
  authToken: string,
  sessionId: string,
): Promise<OkEnvelope> {
  return request(`/api/v1/sessions/${encodeURIComponent(sessionId)}/join`, {
    method: 'POST',
    body: JSON.stringify({}),
    authToken,
  });
}

export async function startAction(
  authToken: string,
  sessionId: string,
): Promise<OkEnvelope> {
  return request(`/api/v1/sessions/${encodeURIComponent(sessionId)}/action/start`, {
    method: 'POST',
    body: JSON.stringify({}),
    authToken,
  });
}

export async function submitActionEvents(
  authToken: string,
  sessionId: string,
  events: CanonicalActionEvent[],
): Promise<{ ok: true; phase_result: { submitted_count: number; buffer_size: number } }> {
  // /action/event uses a slightly different envelope (no full session blob)
  const headers = new Headers({
    'content-type': 'application/json',
    authorization: `Bearer ${authToken}`,
  });
  const res = await fetch(
    `${KTS_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/action/event`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
    },
  );
  const text = await res.text();
  if (!res.ok) {
    throw new KtsV1Error({
      code: 'event_submit_failed',
      message: `submit returned ${res.status}: ${text.slice(0, 200)}`,
      status: res.status,
    });
  }
  try {
    return JSON.parse(text) as { ok: true; phase_result: { submitted_count: number; buffer_size: number } };
  } catch {
    throw new KtsV1Error({
      code: 'parse_error',
      message: `submit returned non-JSON: ${text.slice(0, 200)}`,
      status: res.status,
    });
  }
}

export async function stopAction(
  authToken: string,
  sessionId: string,
  args: {
    duration_s?: number;
    reason?: 'user_finished' | 'user_aborted' | 'timer_expired';
  },
): Promise<OkEnvelope<{ action_receipt?: PhaseReceipt }>> {
  return request<{ action_receipt?: PhaseReceipt }>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/action/stop`,
    {
      method: 'POST',
      body: JSON.stringify(args),
      authToken,
    },
  );
}

export async function finishSession(
  authToken: string,
  sessionId: string,
): Promise<OkEnvelope<{ background_started: boolean; estimated_duration_s: number }>> {
  return request<{ background_started: boolean; estimated_duration_s: number }>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/finish`,
    { method: 'POST', body: JSON.stringify({}), authToken },
  );
}

export async function abandonSession(
  authToken: string,
  sessionId: string,
  reason?: string,
): Promise<OkEnvelope> {
  return request(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/abandon`,
    {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? 'user_cancelled' }),
      authToken,
    },
  );
}

export async function getSession(
  authToken: string,
  sessionId: string,
): Promise<OkEnvelope> {
  return request(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: 'GET',
      authToken,
    },
  );
}

export async function listMySessions(
  authToken: string,
  filters?: { status?: SessionStatus; tool_id?: string; limit?: number },
): Promise<{ ok: true; sessions: KineticSession[]; next_cursor: string | null }> {
  const qs = new URLSearchParams();
  if (filters?.status) qs.set('status', filters.status);
  if (filters?.tool_id) qs.set('tool_id', filters.tool_id);
  if (filters?.limit) qs.set('limit', String(filters.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  const headers = new Headers({ authorization: `Bearer ${authToken}` });
  const res = await fetch(`${KTS_BASE_URL}/api/v1/sessions${suffix}`, { headers });
  const text = await res.text();
  if (!res.ok) {
    throw new KtsV1Error({
      code: 'list_failed',
      message: `list returned ${res.status}`,
      status: res.status,
    });
  }
  return JSON.parse(text) as { ok: true; sessions: KineticSession[]; next_cursor: string | null };
}
